/* eslint-disable */
import { ChocoboFactory } from '@/services/chocoboFactory'
import { JockeyFactory } from '@/services/jockeyFactory'
import { Owner } from '@/models/owner'
import { PerformanceRecordsService } from '@/services/performanceRecordsService'

// Constants for race distances and target times
const RACE_DISTANCES = {
  short: 800, // meters
  medium: 2000,
  long: 5000
};

// Target times in seconds for a perfect S+ grade chocobo
const TARGET_TIMES = {
  short: 43.2, // ~18.5 m/s
  medium: 105.0, // ~16.7 m/s
  long: 340.0 // ~14.7 m/s
};

export class RaceTimeManager {
  static calculateFinishTime(performanceScore, raceLength) {
    // Adjusted scaling: Use logarithmic scaling for scores above 250
    // This rewards high stats while preventing total domination
    let normalizedScore;
    if (performanceScore <= 250) {
      normalizedScore = performanceScore / 250;
    } else {
      // Logarithmic scaling for scores above 250
      // Score 250 = 1.0, Score 350 = ~1.15, Score 450 = ~1.25
      normalizedScore = 1 + (Math.log(performanceScore / 250) * 0.3);
    }
    
    const targetTime = TARGET_TIMES[raceLength];
    // Higher scores = less variance (faster times)
    const variance = Math.max(0, (1 - normalizedScore) * targetTime);
    const randomFactor = 0.98 + (Math.random() * 0.04);
    return (targetTime + variance) * randomFactor;
  }

  static formatRaceTime(timeInSeconds) {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    const milliseconds = Math.floor((timeInSeconds % 1) * 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
  }

  static updatePersonalBest(chocobo, raceLength, finishTime) {
    if (!chocobo.personalBests) {
      chocobo.personalBests = {};
    }
    const currentBest = chocobo.personalBests[raceLength];
    if (!currentBest || finishTime < currentBest) {
      chocobo.personalBests[raceLength] = finishTime;
      return true;
    }
    return false;
  }

  static calculateSpeed(timeInSeconds, raceLength) {
    const distance = RACE_DISTANCES[raceLength];
    return (distance / timeInSeconds).toFixed(1); // m/s
  }
}

export class RaceSimulator {
  static MIN_OPPONENTS = 5;
  static LEVEL_FALLBACK_RANGE = 5;

  static WEIGHT_CONFIGS = {
    short: {
      speedWeight: 0.7,
      staminaWeight: 0.3,
      weightRange: 25
    },
    medium: {
      speedWeight: 0.5,
      staminaWeight: 0.5,
      weightRange: 30
    },
    long: {
      speedWeight: 0.3,
      staminaWeight: 0.7,
      weightRange: 35
    }
  }

  static JOCKEY_FEES = {
    'S': {
      flatFee: 500,
      winningCut: 0.15
    },
    'A': {
      flatFee: 400,
      winningCut: 0.12
    },
    'B': {
      flatFee: 300,
      winningCut: 0.1
    },
    'C': {
      flatFee: 200,
      winningCut: 0.08
    },
    'D': {
      flatFee: 100,
      winningCut: 0.05
    },
    'E': {
      flatFee: 50,
      winningCut: 0.03
    },
    'F': {
      flatFee: 25,
      winningCut: 0.02
    }
  }

  static calculateRacePerformance(chocobo, jockey, raceLength, circuit, currentDay = null) {
    // 1. Start with base chocobo performance - this should be the foundation
    const basePerformance = this.calculateBasePerformance(chocobo, raceLength, currentDay);

    // 2. Apply behavior as a modifier to base performance
    // Better behavior = more consistent, worse behavior = more variable
    const behaviorMultiplier = 0.7 + (chocobo.behavior.improvement_factor / 10);
    
    // Fixed: Randomness should add variability based on behavior quality
    // Good behavior (high multiplier) = less randomness, bad behavior = more randomness
    // Range: ±0.05 for best behavior, ±0.15 for worst behavior
    const maxRandomness = 0.15 - (chocobo.behavior.improvement_factor * 0.01);
    const behaviorRandomness = (Math.random() - 0.5) * 2 * maxRandomness;
    const behaviorModifier = Math.max(0.7, Math.min(1.35, behaviorMultiplier + behaviorRandomness));

    // 3. Apply jockey skill as a smaller modifier (0.9 to 1.1 range)
    const skillModifier = 0.9 + (jockey.skill / 500); // Reduced impact

    // 4. Weight compatibility as a minor modifier (0.95 to 1.0 range)
    const config = this.WEIGHT_CONFIGS[raceLength];
    const optimalWeight = this.calculateOptimalWeight(chocobo, config);
    const weightDiff = Math.abs(jockey.weight - optimalWeight);
    const weightModifier = 1 - Math.min(0.05, (weightDiff / config.weightRange) * 0.05);

    // 5. Region bonus as smallest modifier (0.98 to 1.02 range)
    let regionModifier = 1.0;
    if (circuit && circuit.getEligibleRegions) {
      const eligibleRegions = circuit.getEligibleRegions(chocobo.region);
      regionModifier = eligibleRegions.includes(chocobo.region) ? 1.02 : 0.98;
    }

    // Small random race day factor (0.97 to 1.03)
    const raceDayFactor = 0.97 + (Math.random() * 0.06);

    // Apply modifiers in sequence to maintain proper scaling
    return basePerformance * 
           behaviorModifier * 
           skillModifier * 
           weightModifier * 
           regionModifier * 
           raceDayFactor;
  }

  static calculateBasePerformance(chocobo, raceLength, currentDay = null) {
    const speedWeight = {
      'short': 0.7,
      'medium': 0.5,
      'long': 0.3
    }[raceLength] || 0.5;

    const staminaWeight = 1 - speedWeight;
    
    // Use age-modified stats if currentDay is provided
    const effectiveSpeed = currentDay !== null ? chocobo.getEffectiveSpeed(currentDay) : chocobo.speed;
    const effectiveStamina = currentDay !== null ? chocobo.getEffectiveStamina(currentDay) : chocobo.stamina;
    
    let speedFactor = effectiveSpeed * speedWeight;
    let staminaFactor = effectiveStamina * staminaWeight;
    
    // X-Factor bonus as direct multiplier
    const xFactorBonus = (
      (chocobo.hasSpeedXFactor ? 0.05 : 0) +
      (chocobo.hasStaminaXFactor ? 0.05 : 0)
    );
    
    return (speedFactor + staminaFactor) * (1 + xFactorBonus);
  }

  static calculateOptimalWeight(chocobo, config) {
    const MIN_STAT = 65;
    const MAX_STAT = 200;
    const MIN_WEIGHT = 45;
    const MAX_WEIGHT = 75;
    const STAT_RANGE = MAX_STAT - MIN_STAT;
    const WEIGHT_RANGE = MAX_WEIGHT - MIN_WEIGHT;

    const combinedStats = (chocobo.speed * config.speedWeight) + 
                         (chocobo.stamina * config.staminaWeight);
    
    const normalizedStats = Math.max(MIN_STAT, Math.min(MAX_STAT, combinedStats));
    
    return MIN_WEIGHT + ((normalizedStats - MIN_STAT) / STAT_RANGE) * WEIGHT_RANGE;
  }

  static getJockeyCompatibility(jockey, chocobo, raceLength) {
    const config = this.WEIGHT_CONFIGS[raceLength];
    const optimalWeight = this.calculateOptimalWeight(chocobo, config);
    const weightDiff = Math.abs(jockey.weight - optimalWeight);
    
    return {
      optimalWeight,
      weightDifference: weightDiff,
      compatibilityScore: 100 - ((weightDiff / config.weightRange) * 100),
      expectedConsistency: 100 - (this.calculateConsistencyRange(jockey.skill) * 100),
      recommendation: this.getCompatibilityRecommendation(weightDiff, config.weightRange, jockey.skill)
    };
  }

  static getCompatibilityRecommendation(weightDiff, weightRange, skill) {
    if (weightDiff <= weightRange * 0.2) {
      return skill >= 80 ? "Excellent Match" : "Good Weight, Higher Skill Preferred";
    } else if (weightDiff <= weightRange * 0.4) {
      return skill >= 90 ? "Workable Match" : "Consider Alternative";
    } else {
      return "Poor Match - Find Better Weight Match";
    }
  }

  static calculateConsistencyRange(skill) {
    return Math.max(0.02, 0.20 - (skill / 500));
  }

  static ensureMinimumOpponents(circuit, playerChocobo, availableChocobos, gameStore) {
    let opponents = this.getIdealOpponents(circuit, playerChocobo, availableChocobos);

    if (opponents.length < this.MIN_OPPONENTS) {
      const fallbackOpponents = this.getFallbackOpponents(
        circuit,
        playerChocobo,
        availableChocobos,
        this.MIN_OPPONENTS - opponents.length
      );

      opponents = [...opponents, ...fallbackOpponents];

      if (opponents.length < this.MIN_OPPONENTS) {
        const generatedOpponents = this.generateNewOpponents(
          circuit,
          playerChocobo,
          this.MIN_OPPONENTS - opponents.length,
          gameStore
        );

        generatedOpponents.forEach(chocobo => {
          gameStore.chocobos.push(chocobo);
          const eligibleOwners = gameStore.owners.filter(owner => 
            !owner.isPlayer && 
            owner.money >= circuit.entryFee &&
            owner.chocoboIds.length < 15
          );

          if (eligibleOwners.length > 0) {
            const randomOwner = eligibleOwners[Math.floor(Math.random() * eligibleOwners.length)];
            randomOwner.chocoboIds.push(chocobo.id);
          }
        });
        
        opponents = [...opponents, ...generatedOpponents];
      }
    }

    return opponents;
  }

  static getIdealOpponents(circuit, playerChocobo, availableChocobos) {
    // Filter based purely on circuit requirements, ignoring player chocobo stats
    return availableChocobos.filter(chocobo => 
      chocobo.id !== playerChocobo.id &&
      chocobo.level >= circuit.unlockLevel &&  // Must meet minimum circuit level
      circuit.getEligibleRegions().includes(chocobo.region)
    );
  }

  static getFallbackOpponents(circuit, playerChocobo, availableChocobos, needed) {
    // For regional circuit, generate basic opponents if needed
    if (circuit.id === 'regional' && availableChocobos.length < needed) {
      return this.generateRegionalOpponents(
        circuit,
        needed - availableChocobos.length,
        gameStore
      )
    }

    // Normal fallback for higher circuits
    const minLevel = Math.max(1, circuit.unlockLevel - this.LEVEL_FALLBACK_RANGE)
    
    return availableChocobos
      .filter(chocobo =>
        chocobo.id !== playerChocobo.id &&
        chocobo.level >= minLevel &&
        chocobo.level < circuit.unlockLevel &&
        circuit.getEligibleRegions().includes(chocobo.region)
      )
      .slice(0, needed)
  }

  static generateRegionalOpponents(circuit, count, gameStore) {
    const opponents = []
    
    for (let i = 0; i < count; i++) {
      const level = Math.floor(Math.random() * 3) + 1  // Level 1-3 for regional
      const chocobo = ChocoboFactory.generateHighLevelChocobo(1, 3, gameStore.currentDay)
      
      // Ensure basic stats for regional circuit
      chocobo.baseSpeed = Math.min(chocobo.baseSpeed, 30)
      chocobo.baseStamina = Math.min(chocobo.baseStamina, 30)
      chocobo.updateTotalStats()
      
      // Add to game state
      gameStore.chocobos.push(chocobo)
      
      // Find an owner in the region
      const eligibleOwners = gameStore.owners.filter(owner => 
        !owner.isPlayer && 
        owner.money >= circuit.entryFee &&
        owner.chocoboIds.length < 15 &&
        circuit.getEligibleRegions().includes(owner.homeRegion)
      )

      if (eligibleOwners.length > 0) {
        const randomOwner = eligibleOwners[Math.floor(Math.random() * eligibleOwners.length)]
        randomOwner.chocoboIds.push(chocobo.id)
      }
      
      opponents.push(chocobo)
    }
    
    return opponents
  }

  static generateNewOpponents(circuit, playerChocobo, count, gameStore) {
    const opponents = [];
    const eligibleOwners = gameStore.owners.filter(owner => 
      !owner.isPlayer && 
      owner.money >= circuit.entryFee &&
      owner.chocoboIds.length < 15 &&
      circuit.getEligibleRegions().includes(owner.homeRegion)
    );
  
    // Ensure we have at least one owner
    if (eligibleOwners.length === 0) {
      const emergencyOwner = new Owner(
        'Emergency Stable',
        'Circuit Rental Stable',
        circuit.getEligibleRegions()[0]
      );
      emergencyOwner.money = circuit.entryFee * 10;
      gameStore.owners.push(emergencyOwner);
      eligibleOwners.push(emergencyOwner);
    }
  
    for (let i = 0; i < count; i++) {
      // Generate stats appropriate for the circuit tier
      let minLevel, maxLevel, targetGradeRange;
      
      switch(circuit.id) {
        case 'regional':
          minLevel = 1;
          maxLevel = 15;
          targetGradeRange = ['D+', 'D', 'C-', 'C', 'C+', 'B-'];
          break;
        case 'continental':
          minLevel = circuit.unlockLevel;
          maxLevel = 25;
          targetGradeRange = ['C+', 'B-', 'B', 'B+', 'A-'];
          break;
        case 'transcontinental':
          minLevel = circuit.unlockLevel;
          maxLevel = 35;
          targetGradeRange = ['B+', 'A-', 'A', 'A+'];
          break;
        case 'global':
          minLevel = circuit.unlockLevel;
          maxLevel = 40;
          targetGradeRange = ['A', 'A+', 'S-', 'S'];
          break;
        case 'saucer':
          minLevel = circuit.unlockLevel;
          maxLevel = 40;
          targetGradeRange = ['A+', 'S-', 'S', 'S+'];
          break;
        default:
          minLevel = circuit.unlockLevel;
          maxLevel = circuit.unlockLevel + 10;
          targetGradeRange = ['C', 'B', 'A'];
      }
  
      // Select owner
      const selectedOwner = this.selectWeightedOwner(
        eligibleOwners.map(owner => ({
          owner,
          weight: this.calculateOwnerWeight(owner, circuit)
        }))
      );
  
      // Generate base chocobo within level range
      const chocobo = ChocoboFactory.generateHighLevelChocobo(
        minLevel,
        maxLevel,
        gameStore.currentDay
      );
  
      chocobo.region = selectedOwner.homeRegion;
  
      // Select random grade from appropriate range for circuit
      const targetGrade = targetGradeRange[Math.floor(Math.random() * targetGradeRange.length)];
      const targetValue = this.calculateTargetStatForGrade(targetGrade);
  
      // Adjust stats to match circuit expectations
      chocobo.baseSpeed = Math.floor(targetValue * 0.7);
      chocobo.baseStamina = Math.floor(targetValue * 0.7);
      chocobo.speedBonus = Math.floor(targetValue * 0.3);
      chocobo.staminaBonus = Math.floor(targetValue * 0.3);
      chocobo.updateTotalStats();
  
      // Add to game state
      gameStore.chocobos.push(chocobo);
      selectedOwner.chocoboIds.push(chocobo.id);
      opponents.push(chocobo);
    }
  
    return opponents;
  }

  static formatRaceTime(time) {
    const normalizedTime = (time * 0.5) + 120
    const minutes = Math.floor(normalizedTime / 60)
    const seconds = Math.floor(normalizedTime % 60)
    const milliseconds = Math.floor((normalizedTime % 1) * 1000)
    
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`
  }

  static calculateOwnerWeight(owner, circuit) {
    let weight = 100;
    
    // Reduce weight if stable is getting full
    weight *= (15 - owner.chocoboIds.length) / 15;
    
    // Reduce weight if money is low
    weight *= Math.min(1, owner.money / (circuit.entryFee * 10));
    
    // Bonus for being in circuit's primary region
    if (owner.homeRegion === circuit.getEligibleRegions()[0]) {
      weight *= 1.2;
    }
    
    return weight;
  }
  
  static getSkillGrade(skill) {
    if (skill >= 90) return 'S'
    if (skill >= 80) return 'A'
    if (skill >= 70) return 'B'
    if (skill >= 60) return 'C'
    if (skill >= 50) return 'D'
    if (skill >= 40) return 'E'
    return 'F'
  }

  static selectWeightedOwner(weightedOwners) {
    const totalWeight = weightedOwners.reduce((sum, wo) => sum + wo.weight, 0);
    let random = Math.random() * totalWeight;
    
    for (const weightedOwner of weightedOwners) {
      random -= weightedOwner.weight;
      if (random <= 0) {
        return weightedOwner.owner;
      }
    }
    
    // Fallback to first owner if something goes wrong
    return weightedOwners[0].owner;
  }

  static calculateTargetStatForGrade(grade) {
    const statValues = {
      'S+': 190,
      'S': 180,
      'S-': 170,
      'A+': 150,
      'A': 125,
      'A-': 110,
      'B+': 91,
      'B': 90,
      'B-': 80,
      'C+': 70,
      'C': 60,
      'C-': 50,
      'D+': 40,
      'D': 30,
      'D-': 20,
      'E': 10,
      'F': 5
    };
  
    return statValues[grade] || 50;
  }

  static simulateRace(referenceChocobo, referenceJockey, chocobos, jockeys, raceLength, gameStore, circuit) {
    if (chocobos.length < this.MIN_OPPONENTS + 1) {
      const opponents = this.ensureMinimumOpponents(
        circuit,
        referenceChocobo,
        chocobos.filter(c => c.id !== referenceChocobo.id),
        gameStore
      );
      
      chocobos = [referenceChocobo, ...opponents];
      
      while (jockeys.length < chocobos.length) {
        const availableJockeys = gameStore.jockeys.filter(j => 
          j.recentForm[j.recentForm.length - 1] === null &&
          !jockeys.includes(j)
        );
        
        if (availableJockeys.length > 0) {
          jockeys.push(availableJockeys[0]);
        } else {
          const newJockey = JockeyFactory.generateRandomJockey();
          gameStore.jockeys.push(newJockey);
          jockeys.push(newJockey);
        }
      }
    }

    const performances = chocobos.map((chocobo, index) => ({
      chocobo: chocobo,
      jockey: jockeys[index],
      position: 0,
      finalScore: this.calculateRacePerformance(chocobo, jockeys[index], raceLength, circuit, gameStore.currentDay),
      experienceGained: 0,
      moneyReward: 0
    }));

    performances.forEach(perf => {
      const finishTime = RaceTimeManager.calculateFinishTime(perf.finalScore, raceLength);
      perf.finishTime = finishTime;
      perf.formattedTime = RaceTimeManager.formatRaceTime(finishTime);
      perf.speed = RaceTimeManager.calculateSpeed(finishTime, raceLength);
      
      const isPB = RaceTimeManager.updatePersonalBest(perf.chocobo, raceLength, finishTime);
      if (isPB) {
        perf.personalBest = true;
      }
    });

    performances.sort((a, b) => a.finishTime - b.finishTime);
    
    performances.forEach((perf, index) => {
      perf.position = index + 1;
      
      const rewards = calculateRaceRewards(perf.position, raceLength, circuit);
      perf.experienceGained = rewards.xp;
      perf.moneyReward = rewards.money;

      if (perf.jockey) {
        perf.jockey.updateForm(perf.position);
      }

      perf.chocobo.recordRaceResult(
        perf.position, 
        circuit,
        raceLength,
        perf.finishTime,
        gameStore.currentDay,
        rewards.money
      );
    });

    // Process performance records for top performers tracking
    const raceResult = {
      circuit: circuit,
      raceType: raceLength,
      participants: performances,
      currentDay: gameStore.currentDay
    };
    PerformanceRecordsService.processRaceResult(raceResult, gameStore);

    return {
      results: performances,
      racingChocobos: chocobos
    };
  }
}

export const calculateRaceRewards = (position, raceType, circuit) => {
  const baseRewards = RACE_REWARDS[raceType][position] || RACE_REWARDS[raceType].participation;
  return {
    money: Math.floor(baseRewards.money * circuit.rewardMultiplier),
    xp: Math.floor(baseRewards.xp * circuit.xpMultiplier)
  };
};

export const RACE_REWARDS = {
  short: {
    1: { money: 4000, xp: 20 },
    2: { money: 3000, xp: 15 },
    3: { money: 2500, xp: 12 },
    participation: { money: 0, xp: 5 }
  },
  medium: {
    1: { money: 4500, xp: 25 },
    2: { money: 3500, xp: 18 },
    3: { money: 2500, xp: 15 },
    participation: { money: 0, xp: 5 }
  },
  long: {
    1: { money: 5000, xp: 30 },
    2: { money: 3500, xp: 22 },
    3: { money: 2500, xp: 18 },
    participation: { money: 0, xp: 5 }
  }
};

export const RACE_CONFIGS = {
  short: {
    name: 'Short Race',
    distance: 800,
    speedWeight: 0.7,
    staminaWeight: 0.3,
    baseExperience: 30
  },
  medium: {
    name: 'Medium Race',
    distance: 2000,
    speedWeight: 0.5,
    staminaWeight: 0.5,
    baseExperience: 30
  },
  long: {
    name: 'Long Race',
    distance: 5000,
    speedWeight: 0.3,
    staminaWeight: 0.7,
    baseExperience: 30
  }
};